import { useCallback, useMemo, useRef, useState } from "react"
import dayjs from "dayjs"
import CharacteristicsService from "src/services/characteristics-service"
import { DATE_FORMAT } from "src/utils/constants"

const DEFAULT_STATE = {
    loading: false,
    loadingSections: {
        parametros: false,
        resultado: false,
        pupar: false,
        puMtm: false,
    },
    parametrosData: null,
    resultadoData: null,
    hasResultado: false,
    puparData: [],
    puMtmData: [],
    dataReferencia: null,
}

const isValidDataReferencia = (value) => {
    if (!value || typeof value !== "string") return false
    if (value === "Invalid Date") return false
    return dayjs(value, DATE_FORMAT).isValid()
}

export const useMemoriaCalculo = () => {
    const service = useMemo(() => new CharacteristicsService(), [])
    const [state, setState] = useState(DEFAULT_STATE)
    const requestIdRef = useRef(0)

    const resolveDataReferencia = useCallback(async (cetipIsin) => {
        try {
            const response = await service.getDataMaisRecente(cetipIsin)
            const maisRecente = response?.body?.DataMaisRecente?.[0]
            const parsed = maisRecente ? dayjs(maisRecente) : null

            return parsed?.isValid() ? parsed.format(DATE_FORMAT) : dayjs().format(DATE_FORMAT)
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[useMemoriaCalculo] erro em getDataMaisRecente", error)
            return dayjs().format(DATE_FORMAT)
        }
    }, [service])

    const loadMemoriaCalculo = useCallback(async (cetipIsin, { dataReferencia } = {}) => {
        if (!cetipIsin) {
            requestIdRef.current += 1
            setState(DEFAULT_STATE)
            return
        }

        const requestId = requestIdRef.current + 1
        requestIdRef.current = requestId

        setState({
            ...DEFAULT_STATE,
            loading: true,
            loadingSections: {
                parametros: true,
                resultado: true,
                pupar: true,
                puMtm: true,
            },
        })

        const resolvedDataReferencia = isValidDataReferencia(dataReferencia)
            ? dataReferencia
            : await resolveDataReferencia(cetipIsin)

        const loadSection = async (section, request, getSectionState) => {
            try {
                const response = await request()
                if (requestIdRef.current !== requestId) return

                setState((prev) => ({
                    ...prev,
                    ...getSectionState(response),
                    loadingSections: { ...prev.loadingSections, [section]: false },
                }))
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`[useMemoriaCalculo] erro ao carregar ${section}`, error)

                if (requestIdRef.current === requestId) {
                    setState((prev) => ({
                        ...prev,
                        loadingSections: { ...prev.loadingSections, [section]: false },
                    }))
                }
            }
        }

        await Promise.allSettled([
            loadSection(
                "parametros",
                () => service.getParametersCalc(cetipIsin, resolvedDataReferencia),
                (response) => ({
                    parametrosData: Array.isArray(response?.body) && response.body.length
                        ? response.body[0]
                        : null,
                })
            ),
            loadSection(
                "resultado",
                () => service.getMemoryCalcResultPrice(cetipIsin, resolvedDataReferencia),
                (response) => {
                    const resultado = Array.isArray(response?.body) && response.body.length
                        ? response.body[0]
                        : null

                    return { resultadoData: resultado, hasResultado: Boolean(resultado) }
                }
            ),
            loadSection(
                "pupar",
                () => service.getMemoryCalcPUPAR(cetipIsin, resolvedDataReferencia),
                (response) => ({
                    puparData: Array.isArray(response?.body) ? response.body : [],
                })
            ),
            loadSection(
                "puMtm",
                () => service.getMemoryCalcMTM(cetipIsin, resolvedDataReferencia),
                (response) => ({
                    puMtmData: Array.isArray(response?.body) ? response.body : [],
                })
            ),
        ])

        if (requestIdRef.current === requestId) {
            setState((prev) => ({
                ...prev,
                loading: false,
                dataReferencia: resolvedDataReferencia,
            }))
        }
    }, [service, resolveDataReferencia])

    const exportExcel = useCallback((cetipIsin) => {
        const data = { ...state.resultadoData, ...state.parametrosData }
        if (!cetipIsin || (!state.parametrosData && !state.resultadoData)) return

        return service.exportMemoryExcel(
            cetipIsin,
            data,
            state.puparData,
            state.puMtmData
        )
    }, [service, state])

    return {
        state: {
            ...state,
            data: state.parametrosData || state.resultadoData
                ? { ...state.resultadoData, ...state.parametrosData }
                : null,
        },
        actions: {
            loadMemoriaCalculo,
            exportExcel,
        },
    }
}
